import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Chip,
  Grid,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Alert,
  Paper,
} from "@mui/material";
import { SearchIcon } from "lucide-react";
import { extractTimeFromDateString } from "../../../hooks/Date";

interface EditBookingProps {
  open: boolean;
  onClose: () => void;
  selectedBooking: any;
  editFormValues: any;
  setEditFormValues: (values: any) => void;
  setSelectedDate: (date: string) => void;
  setStartTime: (time: string) => void;
  setEndTime: (time: string) => void;
  isCheckingAvailability: boolean;
  checkAvailabilityAndGetResult: () => Promise<boolean>;
  availabilityStatus: { available: boolean } | null;
  hourlyRate: number;
  setHourlyRate: (rate: number) => void;
  taxPercentage: number;
  setTaxPercentage: (tax: number) => void;
  updatedAmount: number;
  finalAmountWithTax: number;
  saveUpdatedBooking: () => Promise<void>;
  handleCloseEditDialog: () => void;
  hasChanges: () => boolean;
}

const getStatusChipColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "confirmed":
      return "success";
    case "pending":
      return "warning";
    case "send_invoice":
      return "primary";
    case "pay_at_counter":
      return "warning";
    case "group_on":
      return "secondary";
    case "cancelled":
      return "error";
    case "invoice_sent":
      return "info";
    default:
      return "default";
  }
};

const EditBooking: React.FC<EditBookingProps> = ({
  open,
  onClose,
  selectedBooking,
  editFormValues,
  setEditFormValues,
  setSelectedDate,
  setStartTime,
  setEndTime,
  isCheckingAvailability,
  checkAvailabilityAndGetResult,
  availabilityStatus,
  hourlyRate,
  setHourlyRate,
  taxPercentage,
  setTaxPercentage,
  updatedAmount,
  finalAmountWithTax,
  saveUpdatedBooking,
  handleCloseEditDialog,
  hasChanges,
}) => {
  if (!selectedBooking) return null;

  return (
    <Dialog open={open} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
      <DialogTitle>Edit Booking</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Customer Information
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Typography variant="body1">
                {selectedBooking.bookingInfo.userDetails.name}
              </Typography>
              <Chip
                label={selectedBooking.bookingInfo.userDetails.email}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Booking Details
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Lane/Cage: {selectedBooking.bookingInfo.laneOrCage.name}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Booking Date:
                  </Typography>
                  <TextField
                    type="date"
                    fullWidth
                    value={
                      editFormValues?.bookingForDate || selectedBooking.bookingInfo.bookingForDate
                    }
                    onChange={(e) => {
                      const selectedDateStr = e.target.value;
                      setEditFormValues({
                        ...editFormValues,
                        bookingForDate: selectedDateStr,
                      });
                      const [year, month, day] = selectedDateStr
                        .split("-")
                        .map(Number);
                      const localDate = new Date(year, month - 1, day);
                      localDate.setHours(0, 0, 0, 0);
                      setSelectedDate(localDate.toString());
                    }}
                    size="small"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>

                  Status:
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={
                    editFormValues?.bookingStatus ||
                    selectedBooking.bookingInfo.statusHistoryData.length > 0 && selectedBooking.bookingInfo.statusHistoryData[selectedBooking.bookingInfo.statusHistoryData.length - 1].bookingStatus
                  }
                  onChange={(e) =>
                    setEditFormValues({
                      ...editFormValues,
                      bookingStatus: e.target.value,
                    })
                  }
                >
                  {[
                    "confirmed",
                    "pay_at_counter",
                    "cancelled",
                    "invoice_sent",
                    "send_invoice",
                    "group_on",
                  ].map((status) => (
                    <MenuItem key={status} value={status}>
                      <Chip
                        label={status}
                        size="small"
                        color={getStatusChipColor(status) as any}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Start Time:
                </Typography>
                <TextField
                  type="time"
                  fullWidth
                  value={
                    editFormValues?.startTime ||
                    extractTimeFromDateString(
                      selectedBooking.bookingInfo.startTime
                    )
                  }
                  onChange={(e) => {
                    setEditFormValues({
                      ...editFormValues,
                      startTime: e.target.value,
                    });
                    setStartTime(e.target.value);
                  }}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  End Time:
                </Typography>
                <TextField
                  type="time"
                  fullWidth
                  value={
                    editFormValues?.endTime ||
                    extractTimeFromDateString(
                      selectedBooking.bookingInfo.endTime
                    )
                  }
                  onChange={(e) => {
                    setEditFormValues({
                      ...editFormValues,
                      endTime: e.target.value,
                    });
                    setEndTime(e.target.value);
                  }}
                  size="small"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={
                  isCheckingAvailability ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SearchIcon />
                  )
                }
                onClick={checkAvailabilityAndGetResult}
                disabled={isCheckingAvailability}
              >
                Check Availability
              </Button>

              {availabilityStatus && (
                <Alert
                  severity={availabilityStatus.available ? "success" : "error"}
                  sx={{ mt: 2 }}
                >
                  {availabilityStatus.available
                    ? "This time slot is available!"
                    : "This time conflicts with existing bookings"}
                </Alert>
              )}
            </Box>
          </Box>

          {/* Pricing Section */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Pricing Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Hourly Rate ($):
                </Typography>
                <TextField
                  type="number"
                  disabled
                  fullWidth
                  value={
                    selectedBooking.bookingInfo.laneOrCage?.pricePerHour ||
                    hourlyRate
                  }
                  onChange={(e) => setHourlyRate(Number(e.target.value))}
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Tax (%):
                </Typography>
                <TextField
                  type="number"
                  fullWidth
                  value={taxPercentage}
                  onChange={(e) => setTaxPercentage(Number(e.target.value))}
                  size="small"
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Amount Display Section */}
          {(editFormValues?.startTime || editFormValues?.endTime) && (
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Updated Pricing
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, backgroundColor: "#f8f9fa" }}
              >
                <Stack spacing={1}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2">Base Amount:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      ${updatedAmount.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2">
                      Tax ({taxPercentage}%):
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      ${((updatedAmount * taxPercentage) / 100).toFixed(2)}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Final Amount:
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="primary"
                    >
                      ${finalAmountWithTax.toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Note:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={
                editFormValues?.specialRequests !== undefined
                  ? editFormValues.specialRequests
                  : selectedBooking.bookingInfo.specialRequests || ""
              }
              onChange={(e) =>
                setEditFormValues({
                  ...editFormValues,
                  specialRequests: e.target.value,
                })
              }
              placeholder="Enter any notes"
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseEditDialog}>Cancel</Button>
        <Button
          variant="contained"
          onClick={saveUpdatedBooking}
          disabled={!hasChanges()}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBooking;
